import { alpha, Box, IconButton, Typography, Button, TextField, Stack } from "@mui/material";
import { styled, Theme } from "@mui/material/styles";
import { RefObject } from "react";

export const RootContainer = styled(Box)(() => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "100%"
}));

export const ModalContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "end",
  backgroundColor: theme.customColors?.backgroundPrimary,
  width: "70vw",
  height: "85vh",
  borderRadius: "0.5rem",
  border: `1px solid ${theme.customColors.border}`,
  position: "relative",
  [theme.breakpoints.down("md")]: {
    padding: "20px",
    width: "100vw",
    height: "100vh"
  }
}));

export const AgentChatBody = styled(Stack)(() => ({
  display: "flex",
  height: "100%",
  flexGrow: 1
}));
export const FooterContainer = styled(Stack)(({ theme }) => ({
  borderTop: `0.0625rem solid ${theme.customColors.border}`,
  display: "flex",
  padding: "1rem",
  gap: "0.5rem"
}));

export const ModalHeaderContainer = styled(Box)(() => ({
  alignItems: "start",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  padding: "2.5rem 5rem 1rem",
  width: "100%",
  gap: "1rem"
}));

export const TitleContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "start"
}));

export const CloseButton = styled(IconButton)(() => ({
  top: 0,
  right: 0,
  zIndex: 1
}));

export const Divider = styled(Box)(({ theme }) => ({
  width: "100%",
  borderBottom: `1px solid ${theme.customColors.border}`,
  marginTop: "16px",
  marginBottom: "16px"
}));

type MessagesContainerProps = {
  theme: Theme;
  blockUserInput: boolean;
  ref: RefObject<HTMLDivElement>;
  gap: string;
};

export const MessagesContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "blockUserInput"
})(({ theme, blockUserInput }: MessagesContainerProps) => ({
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  overflowY: "scroll",
  overflowX: "hidden",
  pointerEvents: blockUserInput ? "none" : "auto",
  position: "relative",
  paddingX: "5rem",
  paddingBottom: "1.5rem",
  width: "100%",
  height: "100%",
  "&::-webkit-scrollbar": {
    width: "6px"
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "transparent",
    borderRadius: "8px",
    opacity: 0,
    "&:hover": {
      background: alpha(theme.customColors.systemPrimary01,0.2)
    }
  }
}));

export const WelcomeMessageContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  height: "100%",
  width: "100%",
  paddingLeft: "30px",
  paddingRight: "30px"
}));

export const WelcomeMessage = styled(Typography)(() => ({
  textAlign: "center",
  fontSize: "18px",
  userSelect: "none"
}));

export const LoadingHistoryContainer = styled(Typography)(() => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: 3
}));

export const LoadingHistoryMessage = styled(Typography)(() => ({
  fontSize: "14px",
  userSelect: "none"
}));

export const SessionTerminatedMessageContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  paddingTop: "20px",
  paddingBottom: "20px",
  width: "100%"
}));

export const SessionTerminatedMessage = styled(Typography)(({ theme }) => ({
  textAlign: "center",
  color: theme.customColors.textPrimary
}));



export const SidebarRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "20rem",
  minWidth: "260px",
  height: "100%",
  borderRight: `1px solid ${theme.customColors.border}`,
  [theme.breakpoints.down("md")]: {
    width: "100%",
    minWidth: 0,
    borderRight: "none"
  }
}));

export const SidebarHeader = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  padding: "16px 0"
}));

export const SidebarTitleRow = styled(Box)(() => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: "10px",
  paddingBottom: "12px",
  paddingLeft: "16px",
  paddingRight: "16px"
}));

export const NewChatSection = styled(Box)(({ theme }) => ({
  padding: "16px",
  borderTop: `1px solid ${theme.customColors.border}`,
  borderBottom: `1px solid ${theme.customColors.border}`
}));

export const SearchSection = styled(Box)(() => ({
  padding: "0 16px",
  paddingTop: "16px"
}));

export const HeaderRow = styled(Box)(() => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: "10px"
}));

export const HeaderAIIconSm = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "32px",
  height: "32px",
  borderRadius: "4px",
  backgroundColor: theme.customColors.backgroundPrimary,
  color: theme.customColors.textInput,
  fontWeight: 700,
  fontSize: "13px",
  flexShrink: 0
}));

export const SidebarTitle = styled(Typography)(({ theme }) => ({
  color: theme.customColors.textPrimary,
  fontWeight: 700,
  fontSize: "16px"
}));

export const SearchInput = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "0.63rem",
    backgroundColor: theme.customColors.backgroundPrimary,
    color: theme.customColors.textPrimary,
    fontSize: "0.875rem",
    "& fieldset": { 
      borderColor: theme.customColors.border
    },
    "&.Mui-focused fieldset": { 
      borderColor: theme.customColors.borderAccent
    }
  },
  "& .MuiInputBase-input::placeholder": {
    color: theme.customColors.textDisabled ,
    opacity: 1
  }
}));

type ConversationListProps = { withTopBorder?: boolean };

export const ConversationList = styled(Box, {
  shouldForwardProp: (prop) => prop !== "withTopBorder"
})<ConversationListProps>(({ theme, withTopBorder = true }) => ({
  flex: 1,
  overflowY: "auto",
  scrollbarWidth: "thin",
  borderTop: withTopBorder ? `1px solid ${theme.customColors.border}` : "none"
}));

type ConversationItemProps = { active: boolean };

export const ConversationItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== "active"
})<ConversationItemProps>(({ 
  theme
}) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-start",
  gap: "10px",
  padding: "15px 16px",
  cursor: "pointer",
  transition: "background-color 0.1ms ease",
  borderBottom: `1px solid ${theme.customColors.border}`,
  backgroundColor:  theme.customColors.backgroundPrimary,
  "&:hover": {
    backgroundColor: theme.customColors.backgroundPrimaryControl
  },
  "& > svg": {
    marginTop: "2px",
    flexShrink: 0,
    opacity: 0.8
  }
}));

export const ConversationItemContent = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minWidth: 0
}));

export const ConversationTitle = styled(Typography)(({ theme }) => ({
  color: theme.customColors.textPrimary,
  fontWeight: 600,
  fontSize: "13px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis"
}));

export const ConversationPreview = styled(Typography)(({ theme }) => ({
  color: theme.customColors.textPrimary,
  fontSize: "12px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  minHeight: "1.2em"
}));

export const ConversationDate = styled(Typography)(({ theme }) => ({
  color: theme.customColors.textPrimary,
  fontSize: "11px",
  marginTop: "2px",
  minHeight: "1.2em"
}));

export const DeleteIcon = styled(IconButton)(({ theme }) => ({
  color: theme.customColors.accentVariantA,
  opacity: 0,
  transition: "opacity 120ms ease",
  alignSelf: "center",
  ".MuiBox-root:hover > &": {
    opacity: 1
  },
  "@media (hover: none)": {
    opacity: 1
  },
  "&:hover": {
    color: `${theme.customColors.textPrimary} !important`
  }
}));

export const EmptyState = styled(Typography)(({ theme }) => ({
  color: theme.customColors.textPrimary,
  fontSize: "13px",
  textAlign: "center",
  padding: "16px"
}));

export const HistoryDisabledContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "10px",
  padding: "32px 20px",
  textAlign: "center"
}));

export const HistoryDisabledIcon = styled(Box)(({ theme }) => ({
  width: "44px",
  height: "44px",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: theme.customColors.backgroundDisabled,
  color: theme.customColors.textPrimary,
  marginBottom: "4px"
}));

export const HistoryDisabledTitle = styled(Typography)(({ theme }) => ({
  color: theme.customColors.textPrimary,
  fontSize: "14px",
  fontWeight: 600
}));

export const HistoryDisabledSubtitle = styled(Typography)(({ theme }) => ({
  color: theme.customColors.textPrimary,
  fontSize: "12px",
  lineHeight: 1.5,
  maxWidth: "260px"
}));

export const LoadingRow = styled(Box)(() => ({
  display: "flex",
  justifyContent: "center",
  padding: "16px"
}));

export const SidebarFooter = styled(Box)(({ theme }) => ({
  borderTop: `1px solid ${theme.customColors.border}`,
  padding: "12px 16px"
}));

export const SettingsButton = styled(Button)(({ theme }) => ({
  color: theme.customColors.textPrimary,
  textTransform: "none",
  fontWeight: 500,
  fontSize: "14px",
  padding: "6px 8px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  justifyContent: "flex-start"
}));
